import { useState, useEffect, useCallback } from 'react';
import * as listCache from '../services/listCache';

/**
 * Stale-while-revalidate hook for single-item detail pages.
 * Backed by the same listCache used in ListTemplate, so when FormComponent
 * calls listCache.invalidate() after a save, detail pages automatically
 * get fresh data on the next visit.
 *
 * @param {string|null|undefined} url  Full URL to fetch. Pass null/undefined to skip.
 * @returns {{ data, loading, error, refetch }}
 */
const useDetailFetch = (url) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(!!url);
  const [error, setError] = useState(null);
  const [fetchVersion, setFetchVersion] = useState(0);

  useEffect(() => {
    if (!url) return;
    let cancelled = false;

    const cached = listCache.get(url);
    if (cached) {
      setData(cached.data);
      setLoading(false);
    } else {
      setLoading(true);
    }

    if (!cached || cached.isStale) {
      listCache
        .fetchDeduped(url, {})
        .then(raw => {
          if (cancelled) return;
          listCache.set(url, raw);
          setData(raw);
          setLoading(false);
          setError(null);
        })
        .catch(err => {
          if (cancelled) return;
          setLoading(false);
          setError(err);
        });
    }

    return () => { cancelled = true; };
  }, [url, fetchVersion]);

  const refetch = useCallback(() => {
    if (url) listCache.invalidate(url);
    setFetchVersion(v => v + 1);
  }, [url]);

  return { data, loading, error, refetch };
};

export default useDetailFetch;
